/**
 * AccountDetailsCard - the "Account Details" card on the Lead tab (A-10).
 *
 * Paul's Lead card holds the lead journey only (stage, Designation, Status,
 * owner, lead source, NDA, sites). Everything else about the company or plant
 * lives here: name, vertical, industry, tier and target score, phone,
 * website, location, notes, robot brands and the free-form Details.
 *
 * Site rules: a site inherits its company's tier (read-only here, never
 * sent). Its vertical DEFAULTS to its company's but can be overridden on that
 * site only (X-07, 10/1/26 - it reverses the 9/28 "a site always uses its
 * company's vertical"): Metalsa is an automotive supplier, but its Monterrey
 * plant builds data center racks. The picker's first choice, "Same as company
 * (...)", stores no vertical of its own (null), so the site follows its
 * company again; any other choice is stored on the site and shown as
 * "(override)". Filters and exports read the effective vertical (verticalOf).
 * The card does not reuse AccountForm (Edit Account), whose vertical is a
 * company's.
 *
 * `companyVertical` is the site's company's own vertical (the host looks the
 * company up); without it the card reads the API's parent_vertical, and a
 * site with no override falls back to its effective vertical.
 *
 * onSave(fields) resolves to { ok, error } (AppContext.updateAccount); onSaved
 * runs after the Edit form saved (the host shows "Account updated"). A
 * refusal shows in plain English where it happened: under the form for Edit,
 * under the row for the robot brands and Details, which save on click.
 *
 * Kept as the title text "Account Details" with its Edit button as the next
 * sibling, and Save inside the same card (_scratch/r42_leads_verify.js finds
 * them that way).
 */
import { useState } from 'react';
import { Icon } from '../ui/Icon';
import {
  ROBOT_BRANDS,
  VERTICALS,
  TIERS,
  TIER_HINT,
  DETAIL_FIELD_SUGGESTIONS,
  normalizeTier,
  tierFromScore,
  verticalOf,
  pickField,
} from '../../data/crmStages';
import { INPUT, LABEL, BTN_PRIMARY, BTN_CANCEL, ERROR_LINE } from './crmStyles';

const lblSpan = { fontSize: 11, color: '#999', fontWeight: 600 };
const valSpan = { fontSize: 11, color: '#0D0D0D', fontWeight: 700, textAlign: 'right', maxWidth: '60%' };
const viewRow = { display: 'flex', justifyContent: 'space-between', gap: 10, padding: '10px 0', borderBottom: '1px solid #F5F5F5' };
const sel = {
  padding: '6px 10px',
  borderRadius: 6,
  border: '1px solid #DDD',
  fontSize: 11,
  fontFamily: 'Montserrat',
  outline: 'none',
  background: '#fff',
};
const readOnlyNote = { fontSize: 11, color: '#888', padding: '8px 0' };

// blank stays blank - never store '-' (Paul #43)
const orNull = (v) => (v != null && String(v).trim() ? String(v).trim() : null);
const clean = (v) => (v == null || v === '-' ? '' : String(v));
const initialsOf = (name) =>
  String(name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();

export default function AccountDetailsCard({ account, companyVertical, editable = false, onSave, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [fields, setFields] = useState({});
  const [editErr, setEditErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [customForm, setCustomForm] = useState(null); // { key, value }
  const [rowErr, setRowErr] = useState(null); // { row, msg } - the server's reason

  const isSite = !!pickField(account, 'parentAccountId', 'parent_account_id');
  const parentName = pickField(account, 'parentName', 'parent_name');
  const vertical = verticalOf(account);
  // X-07: a site's own vertical (null = it follows its company's) and the company's
  const ownVertical = isSite ? account.vertical || null : null;
  const parentVertical = isSite
    ? (companyVertical ?? pickField(account, 'parentVertical', 'parent_vertical') ?? (ownVertical ? null : vertical))
    : null;
  const tierEff = normalizeTier(pickField(account, 'tierEffective', 'tier_effective') || account.tier);
  const score = pickField(account, 'targetScore', 'target_score');
  const hasScore = score != null && score !== '';
  const custom = account.custom || {};
  const brands = pickField(account, 'robotBrands', 'robot_brands') || [];

  const patch = async (f, rowKey) => {
    setRowErr(null);
    const r = await onSave(f);
    if (r && r.ok === false) setRowErr({ row: rowKey, msg: r.error || 'Could not save - try again.' });
    return r;
  };
  const errUnder = (rowKey) => (rowErr && rowErr.row === rowKey ? <div style={{ ...ERROR_LINE, marginTop: 6 }}>{rowErr.msg}</div> : null);

  const startEdit = () => {
    setFields({
      name: account.name || '',
      industry: clean(account.industry),
      vertical: account.vertical || '',
      tier: normalizeTier(account.tier),
      targetScore: hasScore ? String(score) : '',
      phone: clean(account.phone),
      website: account.website || '',
      location: clean(account.location),
      notes: account.notes || '',
    });
    setEditErr('');
    setEditing(true);
  };

  const saveEdit = async () => {
    if (busy) return;
    const name = String(fields.name || '').trim();
    if (!name) {
      setEditErr("The name can't be blank.");
      return;
    }
    const scoreText = String(fields.targetScore == null ? '' : fields.targetScore).trim();
    const scoreNum = scoreText === '' ? null : Number(scoreText);
    if (scoreNum != null && !Number.isFinite(scoreNum)) {
      setEditErr('The target score is a number from 0 to 100.');
      return;
    }
    // Won / Open / Lost are derived from the opportunities, never typed here
    const out = {
      name,
      initials: initialsOf(name),
      industry: orNull(fields.industry),
      phone: orNull(fields.phone),
      website: orNull(fields.website),
      location: orNull(fields.location),
      notes: orNull(fields.notes),
      opportunityKey: name,
      target_score: scoreNum == null ? null : Math.max(0, Math.min(100, scoreNum)),
    };
    // a site inherits its company's tier; its vertical is its own override, '' (Same as company) clears it (X-07)
    if (isSite) {
      out.tier = null;
      out.vertical = orNull(fields.vertical);
    } else {
      out.tier = orNull(fields.tier);
      out.vertical = orNull(fields.vertical);
    }
    setBusy(true);
    try {
      const r = await onSave(out);
      if (r && r.ok === false) {
        setEditErr(r.error || 'Could not save - try again.');
        return;
      }
      setEditing(false);
      setEditErr('');
      if (onSaved) onSaved();
    } finally {
      setBusy(false);
    }
  };

  const set = (key) => (e) => {
    const v = e.target.value;
    setFields((p) => ({ ...p, [key]: v }));
    setEditErr('');
  };
  const scoreHint = (s) =>
    s != null && s !== '' && tierFromScore(s) ? `score ${s} → Tier ${tierFromScore(s)} (${TIER_HINT[tierFromScore(s)]})` : '';

  const tierText = tierEff ? `Tier ${tierEff}${isSite ? ` (inherited from ${parentName || 'its company'})` : ''}` : '-';
  const verticalText = !isSite
    ? vertical || '-'
    : ownVertical
      ? `${ownVertical} (override)`
      : vertical
        ? `${vertical} (from ${parentName || 'its company'})`
        : 'No vertical yet';
  const scoreText = hasScore
    ? `${score}${!isSite && tierFromScore(score) ? ` → Tier ${tierFromScore(score)} (${TIER_HINT[tierFromScore(score)]})${tierEff && tierEff !== tierFromScore(score) ? `, set to ${tierEff}` : ''}` : ''}`
    : '-';

  return (
    <div style={{ background: '#fff', border: '1px solid #ECECEC', borderRadius: 12, padding: '14px 18px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#0D0D0D' }}>Account Details</div>
        {!editing && editable && (
          <button
            onClick={startEdit}
            style={{ ...BTN_PRIMARY, padding: '5px 12px', fontSize: 10, display: 'flex', alignItems: 'center', gap: 4 }}
          >
            <Icon name="edit" size={10} color="#fff" /> Edit
          </button>
        )}
      </div>

      {editing ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 16px' }}>
          <div>
            <label style={LABEL}>Name</label>
            <input style={INPUT} value={fields.name} onChange={set('name')} />
          </div>
          <div>
            <label style={LABEL}>Vertical</label>
            <select
              style={INPUT}
              value={fields.vertical}
              onChange={set('vertical')}
              title={isSite ? 'Override the vertical for this site only' : undefined}
            >
              {/* a site: blank stores no vertical, so it follows its company's (X-07) */}
              <option value="">{isSite ? `Same as company (${parentVertical || 'none yet'})` : '---'}</option>
              {VERTICALS.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
              {fields.vertical && !VERTICALS.includes(fields.vertical) && (
                <option value={fields.vertical}>{fields.vertical} (old value)</option>
              )}
            </select>
            {isSite && (
              <div style={{ fontSize: 9.5, color: '#888', marginTop: 3 }}>
                Pick another vertical only if this plant differs from {parentName || 'its company'}.
              </div>
            )}
          </div>
          <div>
            <label style={LABEL}>Industry</label>
            <input style={INPUT} value={fields.industry} onChange={set('industry')} />
          </div>
          <div>
            <label style={LABEL}>Tier</label>
            {isSite ? (
              <div style={readOnlyNote}>
                Tier {tierEff || '-'} · inherited from {parentName || 'parent account'}
              </div>
            ) : (
              <select style={INPUT} value={fields.tier} onChange={set('tier')}>
                <option value="">---</option>
                {TIERS.map((t) => (
                  <option key={t} value={t}>
                    Tier {t}
                  </option>
                ))}
                {fields.tier && !TIERS.includes(fields.tier) && <option value={fields.tier}>{fields.tier} (old value)</option>}
              </select>
            )}
          </div>
          <div>
            <label style={LABEL}>Target score</label>
            <input
              type="number"
              min="0"
              max="100"
              placeholder="0-100"
              style={INPUT}
              value={fields.targetScore}
              onChange={set('targetScore')}
            />
            {!isSite && scoreHint(fields.targetScore) && (
              <div style={{ fontSize: 9.5, color: '#888', marginTop: 3 }}>{scoreHint(fields.targetScore)}</div>
            )}
          </div>
          <div>
            <label style={LABEL}>Phone</label>
            <input style={INPUT} value={fields.phone} onChange={set('phone')} />
          </div>
          <div>
            <label style={LABEL}>Website</label>
            <input style={INPUT} value={fields.website} onChange={set('website')} />
          </div>
          <div>
            <label style={LABEL}>Location</label>
            <input style={INPUT} value={fields.location} onChange={set('location')} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={LABEL}>Notes</label>
            <textarea style={{ ...INPUT, minHeight: 50, resize: 'vertical' }} value={fields.notes} onChange={set('notes')} />
          </div>
          {editErr && <div style={{ ...ERROR_LINE, gridColumn: '1 / -1' }}>{editErr}</div>}
          <div style={{ display: 'flex', gap: 8, gridColumn: '1 / -1' }}>
            <button onClick={saveEdit} disabled={busy} style={{ ...BTN_PRIMARY, opacity: busy ? 0.6 : 1 }}>
              Save
            </button>
            <button
              onClick={() => {
                setEditing(false);
                setEditErr('');
              }}
              style={BTN_CANCEL}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', columnGap: 28 }}>
          {[
            ['Company', account.name],
            ['Vertical', verticalText],
            ['Industry', clean(account.industry) || '-'],
            ['Tier', tierText],
            ['Target score', scoreText],
            ['Phone', clean(account.phone) || '-'],
            ['Website', account.website || '-'],
            ['Location', clean(account.location) || '-'],
            ['Notes', account.notes || '-'],
          ].map(([l, v]) => (
            <div key={l} style={{ ...viewRow, ...(l === 'Notes' ? { gridColumn: '1 / -1' } : {}) }}>
              <span style={lblSpan}>{l}</span>
              <span style={{ ...valSpan, whiteSpace: l === 'Notes' ? 'pre-wrap' : undefined }}>{v}</span>
            </div>
          ))}
        </div>
      )}

      {/* robot brands on site: one click adds or removes */}
      <div style={{ ...viewRow, flexDirection: 'column', gap: 6 }}>
        <span style={lblSpan}>Robot brands</span>
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
          {ROBOT_BRANDS.map((b) => {
            const on = brands.includes(b);
            return (
              <button
                key={b}
                disabled={!editable}
                title={editable ? (on ? `Remove ${b}` : `Add ${b}`) : undefined}
                onClick={() => patch({ robot_brands: on ? brands.filter((x) => x !== b) : [...brands, b] }, 'brands')}
                style={{
                  padding: '3px 10px',
                  borderRadius: 14,
                  fontSize: 9.5,
                  fontWeight: 700,
                  fontFamily: 'Montserrat',
                  cursor: editable ? 'pointer' : 'default',
                  border: `1px solid ${on ? '#1B5E20' : '#E4E4E4'}`,
                  background: on ? '#E8F5E9' : '#fff',
                  color: on ? '#1B5E20' : '#999',
                }}
              >
                {b}
              </button>
            );
          })}
        </div>
        {errUnder('brands')}
      </div>

      {/* custom fields: whatever this record needs recorded (no schema change) */}
      <div style={{ ...viewRow, flexDirection: 'column', gap: 6, borderBottom: 'none' }}>
        <span style={lblSpan}>Details</span>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {Object.entries(custom).map(([k, v]) => (
            <span key={k} style={{ fontSize: 10, background: '#F6F8F3', border: '1px solid #E4E8DE', borderRadius: 6, padding: '3px 9px' }}>
              <b>{k}</b>: {String(v)}
              {editable && (
                <button
                  onClick={() => {
                    const c = { ...custom };
                    delete c[k];
                    patch({ custom: c }, 'details');
                  }}
                  title={`Remove ${k}`}
                  style={{ border: 'none', background: 'none', color: '#bbb', cursor: 'pointer', marginLeft: 3 }}
                >
                  ×
                </button>
              )}
            </span>
          ))}
          {Object.keys(custom).length === 0 && !customForm && (
            <span style={{ fontSize: 10.5, color: '#bbb' }}>
              plant type, processes present, buying autonomy, headcount… add what matters
            </span>
          )}
        </div>
        {editable &&
          (customForm ? (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <input
                autoFocus
                list="crm-detail-fields"
                placeholder="field (pick or type)"
                value={customForm.key}
                onChange={(e) => setCustomForm({ ...customForm, key: e.target.value })}
                style={{ ...sel, width: 180 }}
              />
              <datalist id="crm-detail-fields">
                {DETAIL_FIELD_SUGGESTIONS.map((f) => (
                  <option key={f} value={f} />
                ))}
              </datalist>
              <input
                placeholder="value"
                value={customForm.value}
                onChange={(e) => setCustomForm({ ...customForm, value: e.target.value })}
                style={{ ...sel, width: 200 }}
              />
              <button
                onClick={() => {
                  const k = customForm.key.trim();
                  if (k) {
                    patch({ custom: { ...custom, [k]: customForm.value } }, 'details');
                    setCustomForm(null);
                  }
                }}
                style={{
                  padding: '5px 12px',
                  borderRadius: 6,
                  border: 'none',
                  background: '#1B5E20',
                  color: '#fff',
                  fontSize: 10,
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                Add
              </button>
              <button
                onClick={() => setCustomForm(null)}
                style={{
                  padding: '5px 10px',
                  borderRadius: 6,
                  border: '1px solid #DDD',
                  background: '#fff',
                  color: '#666',
                  fontSize: 10,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={() => setCustomForm({ key: '', value: '' })}
              style={{
                alignSelf: 'flex-start',
                padding: '3px 10px',
                borderRadius: 6,
                border: '1px dashed #BBB',
                background: '#fff',
                color: '#888',
                fontSize: 9.5,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              + field
            </button>
          ))}
        {errUnder('details')}
      </div>
    </div>
  );
}
